import { useLocation, useHistory } from 'react-router-dom';

export const isTrue = (value: any) => value !== 'false' && !!value;

const capitalize = (str: string) => {
  if (!str) {
    return str;
  }

  const lower = str.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

function handleSearchParam(searchParam: any) {
  if (searchParam && (searchParam === 'false' || searchParam === 'true')) {
    return isTrue(searchParam);
  }
  return searchParam;
}

function getValueFromOnChangeArg(eventOrChecked: any) {
  return eventOrChecked?.target
    ? eventOrChecked.target.value || ''
    : eventOrChecked;
}

function isArrayParam(params: Record<string, any>, paramName: string) {
  return Object.keys(params).some((key) => key.startsWith(`${paramName}[`));
}

function getArrayValues(params: Record<string, any>, paramName: string) {
  return (
    Object.entries(params)
      .filter(([key]) => key.startsWith(`${paramName}[`))
      .sort(([a], [b]) => a.localeCompare(b))
      // eslint-disable-next-line no-unused-vars
      .map(([_, value]) => handleSearchParam(value))
  );
}

export const useSearchParam = (searchParamName: string) => {
  const location = useLocation();
  const history = useHistory();
  
  // Parse search params from URL
  const searchParams = new URLSearchParams(location.search);
  const paramsObject = Object.fromEntries(searchParams);

  const searchParam = isArrayParam(paramsObject, searchParamName)
    ? getArrayValues(paramsObject, searchParamName)
    : handleSearchParam(paramsObject[searchParamName]);

  const setSearchParam = (eventOrChecked: any) => {
    const newValue = getValueFromOnChangeArg(eventOrChecked);
    const { [searchParamName]: _, ...rest } = paramsObject;

    // Remove any existing array entries for this param
    const cleanedParams = Object.fromEntries(
      Object.entries(rest).filter(
        ([key]) => !key.startsWith(`${searchParamName}[`)
      )
    );

    // Handle array values
    if (Array.isArray(newValue)) {
      const arrayParams = newValue.reduce(
        (acc: Record<string, any>, value: any, index: number) => ({
          ...acc,
          [`${searchParamName}[${index}]`]: value,
        }),
        {}
      );

      const newSearchParams = new URLSearchParams();
      
      // Add cleaned params
      Object.entries(cleanedParams).forEach(([key, value]) => {
        newSearchParams.set(key, String(value));
      });
      
      // Add array params
      Object.entries(arrayParams).forEach(([key, value]) => {
        newSearchParams.set(key, String(value));
      });

      history.push({
        pathname: location.pathname,
        search: newSearchParams.toString(),
      });
    } else {
      const newSearchParams = new URLSearchParams();
      
      // Add cleaned params
      Object.entries(cleanedParams).forEach(([key, value]) => {
        newSearchParams.set(key, String(value));
      });
      
      // Add new param if it has a value
      if (newValue != null) {
        newSearchParams.set(searchParamName, String(newValue));
      }

      history.push({
        pathname: location.pathname,
        search: newSearchParams.toString(),
      });
    }
  };

  return {
    searchParam,
    [searchParamName]: searchParam,
    setSearchParam,
    [`set${capitalize(searchParamName)}`]: setSearchParam,
  };
}; 